import { useEffect } from "react";
import type {
  CommercialBillingCycle,
  CommercialPaymentProvider,
  CommercialSignupMode,
} from "@itaas/pep-contracts";
import { BILLING_CYCLE_OPTIONS } from "../../utils/billing-cycle";

interface TrialModeStepProps {
  value: CommercialSignupMode | null;
  billingCycle: CommercialBillingCycle;
  paymentProvider: CommercialPaymentProvider | null;
  trialDays: number | null;
  onChange: (mode: CommercialSignupMode) => void;
  onBillingCycleChange: (cycle: CommercialBillingCycle) => void;
  onPaymentProviderChange: (provider: CommercialPaymentProvider) => void;
  onBack: () => void;
  onContinue: () => void;
  error: string | null;
}

const COMING_SOON_PAYMENTS: Array<{
  id: CommercialPaymentProvider;
  title: string;
  desc: string;
  icon: "paypal" | "mobile";
}> = [
  {
    id: "paypal",
    title: "PayPal",
    desc: "Compte PayPal ou carte via PayPal Checkout — activation immédiate.",
    icon: "paypal",
  },
  {
    id: "mpesa",
    title: "Mobile Money",
    desc: "M-Pesa, Orange Money, Airtel Money — validation sur votre téléphone.",
    icon: "mobile",
  },
];

function PaymentIcon({ kind }: { kind: "paypal" | "mobile" }) {
  if (kind === "paypal") {
    return (
      <span className="pep-trial-pay-icon pep-trial-pay-icon--paypal" aria-hidden>
        PP
      </span>
    );
  }
  return (
    <span className="pep-trial-pay-icon pep-trial-pay-icon--mobile" aria-hidden>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="18" r="1" fill="currentColor" />
      </svg>
    </span>
  );
}

export function TrialModeStep({
  value,
  billingCycle,
  trialDays,
  onChange,
  onBillingCycleChange,
  onBack,
  onContinue,
  error,
}: TrialModeStepProps) {
  const isTrial = value === "trial" || value === null;
  const trialLabel =
    trialDays && trialDays > 0 ? `Essai gratuit ${trialDays} jours` : "Essai gratuit";

  useEffect(() => {
    if (value !== "trial") {
      onChange("trial");
    }
  }, [onChange, value]);

  return (
    <div className="card border-0 shadow-sm pep-trial-start-card">
      <div className="pep-trial-start-card__header">
        <h2 className="pep-trial-start-card__title">Comment souhaitez-vous démarrer ?</h2>
        <p className="pep-trial-start-card__lead">
          {trialLabel} — facturation et recouvrement par notre équipe après validation
          de votre parcours.
        </p>
      </div>

      <div className="pep-trial-start-section">
        <p className="pep-trial-start-section__label">Option recommandée</p>
        <button
          type="button"
          className={`pep-trial-mode-card pep-trial-mode-card--primary w-100 text-start${isTrial ? " is-selected" : ""}`}
          onClick={() => onChange("trial")}
          aria-pressed={isTrial}
        >
          <span className="pep-trial-mode-card__badge">Recommandé</span>
          <span className="pep-trial-mode-card__title">{trialLabel}</span>
          <span className="pep-trial-mode-card__desc">
            Sans carte bancaire. Provision automatique de votre organisation.
          </span>
          <span className="pep-trial-mode-card__check" aria-hidden={!isTrial}>
            {isTrial ? "✓ Sélectionné" : ""}
          </span>
        </button>
      </div>

      <div className="pep-trial-start-section mt-4">
        <p className="pep-trial-start-section__label">
          Cycle de facturation
          <span className="pep-trial-start-section__hint">Convenu dès l&apos;essai</span>
        </p>
        <p className="pep-trial-billing-intro">
          Choisissez la périodicité retenue pour votre abonnement après la période d&apos;essai.
        </p>
        <div className="row g-2">
          {BILLING_CYCLE_OPTIONS.map((opt) => (
            <div className="col-md-4" key={opt.id}>
              <button
                type="button"
                className={`pep-trial-billing-card w-100 text-start h-100${billingCycle === opt.id ? " is-selected" : ""}`}
                onClick={() => onBillingCycleChange(opt.id)}
                aria-pressed={billingCycle === opt.id}
              >
                {opt.badge ? <span className="pep-trial-billing-card__badge">{opt.badge}</span> : null}
                <span className="pep-trial-billing-card__title">{opt.label}</span>
                <span className="pep-trial-billing-card__desc">{opt.desc}</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="pep-trial-start-divider" role="separator">
        <span>Paiement immédiat</span>
      </div>

      <div className="pep-trial-start-section">
        <p className="pep-trial-start-section__label">
          Activation directe
          <span className="pep-trial-start-section__hint">Bientôt disponible</span>
        </p>
        <div className="row g-3">
          {COMING_SOON_PAYMENTS.map((opt) => (
            <div className="col-md-6" key={opt.id}>
              <div
                className="pep-trial-pay-card pep-trial-pay-card--disabled w-100 text-start"
                role="group"
                aria-label={`${opt.title} — bientôt disponible`}
                aria-disabled="true"
              >
                <div className="pep-trial-pay-card__top">
                  <PaymentIcon kind={opt.icon} />
                  <span className="pep-trial-pay-card__soon">Bientôt</span>
                </div>
                <span className="pep-trial-pay-card__title">{opt.title}</span>
                <span className="pep-trial-pay-card__desc">{opt.desc}</span>
                <span className="pep-trial-pay-card__lock">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zm-3 0H10V6a2 2 0 1 1 4 0v2z" />
                  </svg>
                  Disponible prochainement
                </span>
              </div>
            </div>
          ))}
        </div>
        <p className="pep-trial-start-footnote mb-0">
          PayPal et Mobile Money seront activés après validation commerciale de votre parcours.
          Pour l&apos;instant, démarrez en essai gratuit sans engagement.
        </p>
      </div>

      {error && <div className="alert alert-danger mt-4 mb-0">{error}</div>}

      <div className="pep-trial-start-actions">
        <button type="button" className="btn btn-outline-secondary" onClick={onBack}>
          ← Solutions
        </button>
        <button
          type="button"
          className="btn btn-primary btn-lg flex-grow-1"
          disabled={!isTrial}
          onClick={onContinue}
        >
          Continuer — organisation →
        </button>
      </div>
    </div>
  );
}
